import { Component, OnInit, OnDestroy, NgZone } from "@angular/core";
import { CrudService } from "src/app/services/crud.service";
import { Subscription } from "rxjs";
import { UtilService } from "src/app/services/util.service";
import { ResponseAPI, UserType, User } from "src/models/user.model";
import { LoadingController, AlertController, Platform, ActionSheetController } from "@ionic/angular";
import { loadingProps } from "src/utils/util.functions";
import { AuthService } from "src/app/services/auth.service";
import { TipoCorrida } from "src/models/corrida.model";
import { ActivatedRoute, NavigationExtras, Router } from "@angular/router";
import { StatusCorrida, StatusCorridaFilter } from "src/models/reserva.model";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { Languages } from "../../../models/language.model";

@Component({
    selector: "app-history-races",
    templateUrl: "./history-races.page.html",
    styleUrls: ["./history-races.page.scss"],
    standalone: false
})
export class HistoryRacesPage implements OnDestroy {
  sub: Subscription;
  listHistory = [];
  claim: UserType;
  userType = UserType;
  TipoCorrida = TipoCorrida;
  user: User;
  status = StatusCorrida;
  offset: number;
  isOpenCanceling: boolean;
  formCancelation: UntypedFormGroup;
  filterForm: UntypedFormGroup;
  showModalFilter: boolean = false;
  todosStatus = Object.keys(StatusCorridaFilter).map((key) => ({
    id: StatusCorridaFilter[key],
    name: key,
  }));
  isFiltered: boolean = false;
  Languages = Languages;
  constructor(
    private crud: CrudService,
    private util: UtilService,
    private loadCtrl: LoadingController,
    public alertController: AlertController,
    public router: Router,
    private route: ActivatedRoute,
    private platform: Platform,
    private auth: AuthService,
    private fb: UntypedFormBuilder,
    private _ngZone: NgZone,
    private actionSheetController: ActionSheetController
  ) {
    this.formCancelation = this.fb.group({
      motivoCancelamento: [""],
      id: [""],
      canceladoPor: [this.auth.user.name],
      voltarParaEmDisputa: [false],
    });
  }

  // ── Share trip (incl. driver details) ─────────────────────────────────────

  // Builds the shareable trip summary, including the driver's name, phone,
  // vehicle and plate (test report: Trip History "Share" button).
  buildShareText(item: any): string {
    const driver = item?.motorista;
    const veiculo = driver?.veiculo;
    const lines = [
      "GoSpread trip",
      `Passenger: ${item?.nomePassageiro ?? ""}`,
      `From: ${item?.enderecoOrigem ?? ""}`,
      `To: ${item?.enderecoDestino ?? ""}`,
    ];
    if (driver) {
      lines.push(`Driver: ${driver?.nome ?? ""}`);
      lines.push(`Driver phone: ${driver?.celular ?? ""}`);
      if (veiculo) {
        const carParts = [veiculo?.ano, veiculo?.marca, veiculo?.modelo, veiculo?.cor]
          .filter(Boolean)
          .join(" ");
        lines.push(`Vehicle: ${carParts}`);
        lines.push(`Plate: ${veiculo?.placa ?? ""}`);
      }
    }
    return lines.join("\n");
  }

  async shareTrip(item: any): Promise<void> {
    await this._shareWithFallback(this.buildShareText(item));
  }

  // Consistent Share visibility (22/06/2026 report 4.1): show Share for any ride
  // with meaningful trip info — accepted, in progress, or finished — regardless
  // of whether a driver object is attached (buildShareText degrades gracefully).
  canShare(item: any): boolean {
    return item?.statusCorrida === StatusCorrida.ACCEPTED
      || item?.statusCorrida === StatusCorrida.STARTED
      || item?.statusCorrida === StatusCorrida.FINISHED;
  }

  private async _shareWithFallback(text: string): Promise<void> {
    const nav = navigator as any;
    try {
      if (nav?.share) {
        await nav.share({ title: "GoSpread", text });
        return;
      }
    } catch (_) {
      // user cancelled or share unsupported — fall through to clipboard
    }
    try {
      if (nav?.clipboard?.writeText) {
        await nav.clipboard.writeText(text);
      }
      await this.util.showToast("Trip details copied to clipboard.");
    } catch (_) {
      await this.util.showToast("Unable to share. Please copy manually.");
    }
  }

  async ionViewWillEnter() {
    this.filterForm = this.fb.group({
      nomeCliente: [""],
      dataInicio: [""],
      dataFim: [""],
      status: [""],
    });
    this.offset = this.changeTimezone(
      new Date(
        new Date().toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })
      ),
      "America/New_York"
    ).getTimezoneOffset();

    this.claim = await this.auth.getClaim();
    this.user = this.auth.user;

    await this.loadData(null, false);
  }

  async cancelRace(trip) {
    console.log(trip);
    if (this.claim === this.userType.Caller || !trip?.dataAgendamentoCorrida) {
      this.openCancel(trip);
    } else {
      const sheet = await this.actionSheetController.create({
        header: "Cancel trip",
        subHeader: "Select the type of cancellation",
        buttons: [
          {
            text: "Cancel trip",
            handler: () => {
              this.openCancel(trip);
            },
          },
          {
            text: "Send trip back to In Dispute",
            handler: () => {
              this.formCancelation = this.fb.group({
                motivoCancelamento: ["I'm not able to do this trip"],
                id: [trip.id],
                canceladoPor: [this.auth.user.name],
                voltarParaEmDisputa: [true],
              });
              this.postReason();
            },
          },
          {
            text: "Cancel",
            role: "cancel",
          },
        ],
      });
      await sheet.present();
    }
  }

  private openCancel(trip: any) {
    this.formCancelation = this.fb.group({
      motivoCancelamento: [""],
      id: [trip.id],
      canceladoPor: [this.auth.user.name],
    });
    this._ngZone.run(() => {
      this.isOpenCanceling = true;
    });
  }

  async postReason() {
    if (this.formCancelation.invalid) {
      return false;
    }
    await this.showLoading();
    this.crud
      .postData("Corrida/cancelar-corrida", null, this.formCancelation.value)
      .subscribe(
        async (response: ResponseAPI) => {
          if (response.success) {
            this.isOpenCanceling = false;
            await this.loadData(null, false);
            await this.loadCtrl.dismiss();
            await this.util.showToast("Your information has been sent.");
          }
        },
        async (error) => {
          this.isOpenCanceling = false;
          await this.loadData(null, false);
          await this.loadCtrl.dismiss();
          await this.util.showToast("An error occured");
        }
      );
  }
  private changeTimezone(date, ianatz) {
    // suppose the date is 12:00 UTC
    var invdate = new Date(
      date.toLocaleString("en-US", {
        timeZone: ianatz,
      })
    );

    // then invdate will be 07:00 in Toronto
    // and the diff is 5 hours
    var diff = date.getTime() - invdate.getTime();

    // so 12:00 in Toronto is 17:00 UTC
    return new Date(date.getTime() - diff); // needs to substract
  }
  async completeRoundTrip(race) {
    await this.showLoading();
    this.crud.getData("Corrida/continuar-corrida?id=" + race.id).subscribe(
      async (response: ResponseAPI) => {
        if (response.success) {
          const roundTrip = response.data;
          if (roundTrip.raceType.includes("Scheduled")) {
            this.crud
              .postData("sms/send-sms", null, {
                numeroDestinatario: roundTrip.celular,
                mensagem:
                  `Your driver ${roundTrip.motorista.nome} is on the way. ` +
                  `Car: ${roundTrip.motorista.veiculo.marca} ${roundTrip.motorista.veiculo.modelo}. License plate: ${roundTrip.motorista.veiculo.placa} ` +
                  `Color: ${roundTrip.motorista.veiculo.cor}`,
              })
              .subscribe(
                (response: any) => { },
                () => { }
              );
          }

          localStorage.setItem("currentRace", JSON.stringify(roundTrip));
          await this.loadCtrl.dismiss();
          await this.router.navigate(["/driver", "start"], { relativeTo: this.route, skipLocationChange: true });
        }
      },
      async (error) => {
        await this.loadCtrl.dismiss();
        await this.util.showToast(
          "Error retrieving data. Please check your internet connection"
        );
      }
    );
  }

  async continueTrip(race) {
    await this.showLoading();
    this.crud.getData("Corrida/" + race.id).subscribe(
      async (response: ResponseAPI) => {
        if (response.success) {
          const trip = response.data;
          localStorage.setItem("currentRace", JSON.stringify(trip));
          await this.loadCtrl.dismiss();
          await this.router.navigate(["/driver", "current-race"], { relativeTo: this.route, skipLocationChange: true });
        }
      },
      async (error) => {
        await this.loadCtrl.dismiss();
        await this.util.showToast(
          "Error retrieving data. Please check your internet connection"
        );
      }
    );
  }

  async newRaceCaller(race) {
    let navigationExtras: NavigationExtras = {
      queryParams: {
        race: JSON.stringify(race),
      },
    };
    await this.router.navigate(["call", "call-driver"], navigationExtras);
  }

  getCardColor(item) {
    if (item.statusCorrida === this.status.CANCELLED) return "CANCELLED";
    if (item.statusCorrida === this.status.ACCEPTED) return "ACCEPTED";
    if (item.statusCorrida === this.status.PENDING) return "REQUESTED";
    if (item.statusCorrida === this.status.FINISHED) {
      if (item.tipoCorrida === TipoCorrida.IDAVOLTA) return "ROUNDTRIP";
      if (item.masterReceber && item.masterReceber.id === this.user.id)
        return "MASTER";
      return "FINISHED";
    }
  }

  async newRaceDriver(race) {
    try {
      await this.showLoading();
      const liberado = <boolean>(
        await this.crud.getData("motorista/liberado").toPromise()
      );
      if (!liberado) {
        const alert = await this.alertController.create({
          mode: this.platform.is("ios") ? "ios" : "md",
          header: "Alert",
          message:
            "You have pending issues to resolve. Please, contact GoSpread.",
          buttons: ["Ok"],
        });
        await alert.present();
        this.loadCtrl.dismiss();
        return;
      }
      const canEnterQueue = <boolean>(
        await this.crud.getData("Motorista/periodo-disponivel").toPromise()
      );
      if (!canEnterQueue) {
        const alert = await this.alertController.create({
          mode: this.platform.is("ios") ? "ios" : "md",
          header: "Warning",
          message:
            "Your use period for the queue has ended. Please, contact GoSpread.",
          buttons: ["Ok"],
        });
        await alert.present();
        this.loadCtrl.dismiss();
        return;
      }
      const pendingWeeklyValue = <boolean>(
        await this.crud
          .getData("motorista/pagamento-plataforma-pendente")
          .toPromise()
      );
      if (pendingWeeklyValue) {
        const alert = await this.alertController.create({
          mode: this.platform.is("ios") ? "ios" : "md",
          header: "Alert",
          message:
            "You have unpaid weekly fee. Please make a transfer of the pending value before proceeding.",
          buttons: ["Ok"],
        });
        await alert.present();
        this.loadCtrl.dismiss();
        return;
      }
      const pendingValue = <boolean>(
        await this.crud.getData("corrida/valor-pendente").toPromise()
      );
      if (pendingValue) {
        const alert = await this.alertController.create({
          mode: this.platform.is("ios") ? "ios" : "md",
          header: "Warning",
          message:
            "You are blocked now. You have a trip fee exceeding $50.00. You must make a payment of your pending fees before proceeding.",
          buttons: ["Ok"],
        });
        await alert.present();
        this.loadCtrl.dismiss();
        return;
      }
      const upcomingRace = <boolean>(
        await this.crud.getData("Corrida/corrida-agendada-breve").toPromise()
      );
      if (upcomingRace) {
        const alert = await this.alertController.create({
          mode: this.platform.is("ios") ? "ios" : "md",
          header: "Alert",
          message:
            "You have an unfinished scheduled trip. Complete this trip first before entering the queue.",
          buttons: ["Ok"],
        });
        await alert.present();
        this.loadCtrl.dismiss();
        return;
      }
      let navigationExtras: NavigationExtras = {
        queryParams: {
          race: JSON.stringify(race),
        },
      };
      await this.loadCtrl.dismiss();
      await this.router.navigate(["call", "call-driver"], navigationExtras);
    } catch (error) {
      await this.loadCtrl.dismiss();
      await this.util.showToast(
        "Error retrieving data. Please check your internet connection"
      );
    }
  }
  setDestinyComplete(destiny: object): string {
    return `${destiny["nomeRua"]}, ${destiny["bairro"]}`;
  }

  async loadData(event: any, filtered: boolean) {
    await this.showLoading();

    if (!filtered) {
      this.filterForm.reset();
    }
    this.sub = this.crud
      .postData("Corrida/historico", null, this.filterForm.value)
      .subscribe(
        async (response: ResponseAPI) => {
          if (response.success) {
            this.isFiltered = filtered;
            this.listHistory = response.data;
            if (event) {
              setTimeout(() => {
                event.target.complete();
              }, 2000);
            }

            await this.loadCtrl.dismiss();
          }
        },
        async (error) => {
          await this.loadCtrl.dismiss();
          if (event) {
            setTimeout(() => {
              event.target.complete();
            }, 2000);
          }
        }
      );
  }

  async showLoading() {
    loadingProps.mode = this.platform.is("ios") ? "ios" : "md";
    const loading = await this.loadCtrl.create(loadingProps);
    return await loading.present();
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }
  showFilter() {
    this.filterForm.reset();
    this.showModalFilter = true;
  }

  filterData(event, filtered: boolean) {
    this.loadData(event, filtered);
    this.showModalFilter = false;
  }
}
